import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import FlameMark from '../Brand/FlameMark';
import ui from '../../styles/ui.module.css';
import CopyButton from './CopyButton';
import { LinkStep, PassphraseStep } from './ShareSteps';
import styles from './Secret.module.css';

interface SecretCreatedProps {
  secretLink: string;
  statusLink: string;
  expiresAt: string;
  views: number;
  /** Empty when the secret has none. Kept only in this tab, until "Create another". */
  passphrase: string;
  onAnother: () => void;
}

/**
 * Shown after create, as separate cards: (1) send the link, (2) send the
 * passphrase another way, when there is one, then a quiet strip with the
 * sender's private status link.
 */
export default function SecretCreated({ secretLink, statusLink, expiresAt, views, passphrase, onAnother }: SecretCreatedProps) {
  const { t, i18n } = useTranslation();
  const expires = new Date(expiresAt).toLocaleString(i18n.language, { dateStyle: 'medium', timeStyle: 'short' });
  const heading = useRef<HTMLHeadingElement>(null);
  const hasPassphrase = passphrase.length > 0;

  // This replaces the form, taking the focused submit button with it: start
  // keyboard and screen-reader users at the result.
  useEffect(() => heading.current?.focus(), []);

  return (
    <section className={styles.created} aria-labelledby="created-title">
      <header className={styles.createdHeader}>
        <h2 id="created-title" className={styles.createdTitle} tabIndex={-1} ref={heading}>
          {t('secret.created.title')}
        </h2>
        <p className={styles.burnLine}>
          <span className={styles.burnMark}>
            <FlameMark size={16} tone="onAccent" />
            {t('secret.created.badge', { count: views })}
          </span>
          <span className={ui.hint}>{t('secret.created.expires', { when: expires })}</span>
        </p>
      </header>

      <LinkStep
        link={secretLink}
        step={hasPassphrase ? 1 : null}
        text={t(hasPassphrase ? 'secret.share.linkTextPassphrase' : 'secret.share.linkText')}
      />

      {hasPassphrase && (
        <>
          <p className={styles.between} aria-hidden="true">
            <span>{t('secret.created.between')}</span>
          </p>
          <PassphraseStep passphrase={passphrase} />
        </>
      )}

      <section className={styles.privateStrip} aria-labelledby="status-title">
        <div className={styles.privateText}>
          <h3 id="status-title" className={styles.privateTitle}>
            {t('secret.created.statusTitle')}
          </h3>
          <p className={ui.hint}>{t('secret.created.statusHint')}</p>
        </div>
        <a className={styles.stripButton} href={statusLink} target="_blank" rel="noopener noreferrer">
          {t('secret.created.openStatus')}
        </a>
        <CopyButton value={statusLink} label={t('secret.copy')} accessibleLabel={t('secret.created.copyStatus')} className={styles.stripButton} />
      </section>

      <button type="button" className={styles.another} onClick={onAnother}>
        {t('secret.created.another')}
      </button>
    </section>
  );
}
